import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Select, MenuItem, Chip, IconButton, Tooltip } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ROLES, DEFAULT_ROLE, roleColor } from './permissionsConfig';
import { CATALOG_PARENTS, descendantsOf, hasGrant } from './permissionCatalog';
import RolesManagerDialog from './RolesManagerDialog';
import SftGlobalData from './SftGlobalData';

// Admin-restriction framework. A single `can(key)` drives all gating —
// screens, sub-tabs AND individual fields (the key namespace already supports
// `<area>.field.<x>`, so adding a field exclusion later = add a key + wrap the
// field in `can()`; nothing else changes).
//
// Role DEFINITIONS are shared via the backend (auth route=webshoproles, lic
// DB) since STEP-2a; localStorage is only the cache/offline fallback. Per-user
// ASSIGNMENT is a deliberate later step: until then every user is DEFAULT_ROLE
// (full access), so production behaviour is unchanged; gating only bites while
// a super-user simulates a restricted role.

const STORAGE_KEY = 'sft_role_defs_v3';

// Persisted shape: the non-owner roles as { key: { label, allow:[...] } }.
// Owner is always code-defined ('*') — never stored, never editable.
const loadStoredRoles = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : null;
    } catch (e) {
        return null;
    }
};

const buildRoleDefs = (stored) => {
    const nonOwner = (stored && Object.keys(stored).length)
        ? stored
        : {
            manager: { label: ROLES.manager.label, allow: ROLES.manager.allow },
            editor:  { label: ROLES.editor.label,  allow: ROLES.editor.allow },
        };
    return { owner: { label: ROLES.owner.label, allow: '*' }, ...nonOwner };
};

const persistRoles = (defs) => {
    const nonOwner = {};
    Object.entries(defs).forEach(([k, d]) => { if (d.allow !== '*') nonOwner[k] = { label: d.label, allow: d.allow }; });
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(nonOwner)); } catch (e) { /* ignore */ }
};

const newRoleKey = () => 'role_' + Date.now().toString(36) + Math.floor(Math.random() * 1e4).toString(36);

// ── Server sync (permissions STEP-2a) ───────────────────────────────────
// Role DEFINITIONS live in the lic DB (auth route=webshoproles) so every
// super-user manages the SAME set; localStorage stays as cache/fallback.
// Per-user assignment is a deliberate later step. Edits are pushed with a
// short debounce (checkbox toggles fire per click); failures are silent —
// the local state/cache keeps working and the next successful save syncs.
const _syncTimers = {};
const pushRoleToServer = (key, def) => {
    clearTimeout(_syncTimers[key]);
    _syncTimers[key] = setTimeout(() => {
        SftGlobalData.post('webshoproles&action=save', {
            role_key: key,
            label:    def.label || key,
            allow:    Array.isArray(def.allow) ? def.allow : [],
        }).catch(() => { /* cache covers it */ });
    }, 800);
};
const deleteRoleOnServer = (key) => {
    clearTimeout(_syncTimers[key]);
    SftGlobalData.post('webshoproles&action=delete', { role_key: key }).catch(() => {});
};
const serverRowsToDefs = (rows) => {
    const defs = {};
    rows.forEach(r => {
        if (r && r.role_key) defs[r.role_key] = { label: r.label || r.role_key, allow: Array.isArray(r.allow) ? r.allow : [] };
    });
    return defs;
};

const PermissionsContext = createContext({
    can: () => true,
    role: DEFAULT_ROLE,
    simulatedRole: null,
    isSimulating: false,
    setSimulatedRole: () => {},
    roleDefs: ROLES,
    updateRoleAllow: () => {},
    addRole: () => {},
    renameRole: () => {},
    deleteRole: () => {},
    duplicateRole: () => {},
});

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider = ({ showSimulator = false, t, muiTheme, children }) => {
    // null = use the real (assigned) role; a role key = preview that role.
    const [simulatedRole, setSimulatedRole] = useState(null);
    const [roleDefs, setRoleDefs] = useState(() => buildRoleDefs(loadStoredRoles()));
    const [managerOpen, setManagerOpen] = useState(false);

    // Load the shared role set from the backend once; localStorage/defaults
    // remain the fallback when the call fails. An EMPTY server set is seeded
    // with the current local defs (one-time migration of the localStorage era).
    React.useEffect(() => {
        let cancelled = false;
        SftGlobalData.post('webshoproles&action=list', {}).then(res => {
            if (cancelled || !res?.success || !Array.isArray(res.data)) return;
            if (res.data.length === 0) {
                setRoleDefs(prev => {
                    Object.entries(prev).forEach(([k, d]) => {
                        if (d.allow !== '*') pushRoleToServer(k, d);
                    });
                    return prev;
                });
                return;
            }
            const defs = { owner: { label: ROLES.owner.label, allow: '*' }, ...serverRowsToDefs(res.data) };
            setRoleDefs(defs);
            persistRoles(defs);
        }).catch(() => { /* offline/failure → local cache stands */ });
        return () => { cancelled = true; };
    }, []);

    // Real role for now: everyone is full-access (DEFAULT_ROLE) until the
    // backend per-user model lands. Swap this for the user's assigned role then.
    const realRole = DEFAULT_ROLE;
    const effectiveRole = (simulatedRole && roleDefs[simulatedRole]) ? simulatedRole : realRole;

    const can = useMemo(() => {
        const def = roleDefs[effectiveRole] || roleDefs[DEFAULT_ROLE];
        const allow = def ? def.allow : '*';
        if (allow === '*') return () => true;
        const set = new Set(Array.isArray(allow) ? allow : []);
        return (key) => {
            if (hasGrant(set, key)) return true;
            // Unknown key (not in the catalog) → fail-open: never hides UI.
            if (!(key in CATALOG_PARENTS)) return true;
            // A container stays visible if any descendant is granted, so a
            // partially-granted screen/tab remains navigable.
            return descendantsOf(key).some(d => set.has(d));
        };
    }, [roleDefs, effectiveRole]);

    // All mutations persist immediately. Functional updates → stable handlers
    // (empty deps), so the context value only changes when roleDefs really does.
    const updateRoleAllow = useCallback((key, allow) => {
        setRoleDefs(prev => {
            if (!prev[key] || prev[key].allow === '*') return prev;
            const next = { ...prev, [key]: { ...prev[key], allow } };
            persistRoles(next);
            pushRoleToServer(key, next[key]);
            return next;
        });
    }, []);
    const addRole = useCallback((label) => {
        const key = newRoleKey();
        setRoleDefs(prev => {
            const next = { ...prev, [key]: { label: label || 'Nieuwe rol', allow: [] } };
            persistRoles(next);
            pushRoleToServer(key, next[key]);
            return next;
        });
        return key;
    }, []);
    const duplicateRole = useCallback((key) => {
        const nk = newRoleKey();
        setRoleDefs(prev => {
            const src = prev[key];
            if (!src || src.allow === '*') return prev;
            const next = { ...prev, [nk]: { label: `${src.label || 'Rol'} (kopie)`, allow: Array.isArray(src.allow) ? [...src.allow] : [] } };
            persistRoles(next);
            pushRoleToServer(nk, next[nk]);
            return next;
        });
        return nk;
    }, []);
    const renameRole = useCallback((key, label) => {
        setRoleDefs(prev => {
            if (!prev[key] || prev[key].allow === '*') return prev;
            const next = { ...prev, [key]: { ...prev[key], label } };
            persistRoles(next);
            pushRoleToServer(key, next[key]);
            return next;
        });
    }, []);
    const deleteRole = useCallback((key) => {
        setRoleDefs(prev => {
            if (!prev[key] || prev[key].allow === '*') return prev;
            const next = { ...prev };
            delete next[key];
            persistRoles(next);
            deleteRoleOnServer(key);
            return next;
        });
        setSimulatedRole(cur => (cur === key ? null : cur));
    }, []);

    const value = useMemo(() => ({
        can,
        role: effectiveRole,
        simulatedRole,
        isSimulating: !!simulatedRole,
        setSimulatedRole,
        roleDefs,
        updateRoleAllow,
        addRole,
        renameRole,
        deleteRole,
        duplicateRole,
    }), [can, effectiveRole, simulatedRole, roleDefs, updateRoleAllow, addRole, renameRole, deleteRole, duplicateRole]);

    // The simulator widget + manager dialog live above the app's own
    // ThemeProvider, so re-apply the active MUI theme here — otherwise they'd
    // render with MUI's default light theme even in dark mode.
    const overlay = showSimulator ? (
        <>
            {/* Hide the floating widget while the manager dialog is open — it
                sits above the dialog (zIndex 1400 > modal 1300) otherwise. */}
            {!managerOpen && (
                <RoleSimulator
                    t={t}
                    simulatedRole={simulatedRole}
                    effectiveRole={effectiveRole}
                    roleDefs={roleDefs}
                    onChange={setSimulatedRole}
                    onOpenManager={() => setManagerOpen(true)}
                />
            )}
            {/* Mounted only while open (F07): buildCatalogNodes walks every
                card anchor with t() per node — wasted while closed. */}
            {managerOpen && <RolesManagerDialog
                open={managerOpen}
                onClose={() => setManagerOpen(false)}
                roleDefs={roleDefs}
                onChangeRole={updateRoleAllow}
                onAddRole={addRole}
                onRenameRole={renameRole}
                onDeleteRole={deleteRole}
                onDuplicateRole={duplicateRole}
                t={t}
            />}
        </>
    ) : null;

    return (
        <PermissionsContext.Provider value={value}>
            {children}
            {overlay && (muiTheme ? <ThemeProvider theme={muiTheme}>{overlay}</ThemeProvider> : overlay)}
        </PermissionsContext.Provider>
    );
};

PermissionsProvider.propTypes = {
    showSimulator: PropTypes.bool,
    t: PropTypes.func,
    muiTheme: PropTypes.object,
    children: PropTypes.node,
};

// Floating dev/test widget (super-users, webshop screens only) to preview the
// admin as a role and to open the role manager. Collapsible: a small launcher
// pill (bottom-right, above the config save FAB and clear of the left sidebar)
// that reveals the panel on hover/click; collapse via the chevron.
const RoleSimulator = ({ simulatedRole, effectiveRole, roleDefs, onChange, onOpenManager, t }) => {
    const tr = (key, fb) => (typeof t === 'function' ? t(key, fb) : fb);
    const active = !!simulatedRole;
    const [open, setOpen] = useState(false);

    return (
        <Box sx={{ position: 'fixed', right: 24, bottom: 88, zIndex: 1400, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            {open ? (
                <Paper
                    elevation={6}
                    sx={{
                        px: 1.5, py: 1, borderRadius: 2, width: 280,
                        border: active ? '2px solid' : '1px solid',
                        borderColor: active ? 'warning.main' : 'divider',
                        backgroundColor: active ? 'rgba(237,108,2,0.06)' : 'background.paper',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <VisibilityIcon fontSize="small" color={active ? 'warning' : 'disabled'} />
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3, flex: 1 }}>
                            {tr('roles_simulate', 'Simuleer rol')}
                        </Typography>
                        {active && <Chip size="small" color="warning" label={tr('roles_sim_active', 'actief')} sx={{ height: 18, fontSize: '0.65rem' }} />}
                        <Tooltip title={tr('roles_title', 'Rollen beheren')}>
                            <IconButton size="small" onClick={onOpenManager}>
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={tr('roles_sim_collapse', 'Inklappen')}>
                            <IconButton size="small" onClick={() => setOpen(false)}>
                                <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Select
                        size="small"
                        fullWidth
                        value={simulatedRole || ''}
                        displayEmpty
                        onChange={(e) => onChange(e.target.value || null)}
                        sx={{ fontSize: '0.85rem' }}
                        MenuProps={{ sx: { zIndex: 1500 } }}
                    >
                        <MenuItem value=""><em>{tr('roles_sim_real_role', 'Echte rol (volledige toegang)')}</em></MenuItem>
                        {Object.entries(roleDefs).map(([key, def]) => (
                            <MenuItem key={key} value={key}>
                                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: roleColor(key), mr: 1, flexShrink: 0 }} />
                                {def.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {active && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', lineHeight: 1.3 }}>
                            {tr('roles_sim_viewing_as', 'Je bekijkt de admin als')} <b>{roleDefs[effectiveRole]?.label || effectiveRole}</b>. {tr('roles_sim_preview_note', 'Enkel een voorvertoning — je eigen rechten wijzigen niet.')}
                        </Typography>
                    )}
                </Paper>
            ) : (
                <Paper
                    elevation={6}
                    onClick={() => setOpen(true)}
                    onMouseEnter={() => setOpen(true)}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        px: 1.25, py: 0.5, borderRadius: 5, cursor: 'pointer',
                        border: active ? '2px solid' : '1px solid',
                        borderColor: active ? 'warning.main' : 'divider',
                        backgroundColor: active ? 'rgba(237,108,2,0.10)' : 'background.paper',
                        '&:hover': { boxShadow: 4 },
                    }}
                >
                    <VisibilityIcon fontSize="small" color={active ? 'warning' : 'action'} />
                    {active && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: roleColor(effectiveRole), flexShrink: 0 }} />}
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {active ? (roleDefs[effectiveRole]?.label || tr('roles_role', 'Rol')) : tr('roles_simulate', 'Simuleer rol')}
                    </Typography>
                    <KeyboardArrowUpIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </Paper>
            )}
        </Box>
    );
};

RoleSimulator.propTypes = {
    t: PropTypes.func,
    simulatedRole: PropTypes.string,
    effectiveRole: PropTypes.string,
    roleDefs: PropTypes.object,
    onChange: PropTypes.func,
    onOpenManager: PropTypes.func,
};
